import { useRef, useState } from "react";
import api from "@/lib/api";
import { toast } from "sonner";
import { Paperclip, X, Loader2, FileText, RefreshCw } from "lucide-react";
import AttachmentPreview from "@/components/AttachmentPreview";

const fmtSize = (n) => {
  if (n == null) return "";
  if (n < 1024 * 1024) return `${Math.max(1, Math.round(n / 1024))} KB`;
  return `${(n / (1024 * 1024)).toFixed(1)} MB`;
};

/** Chips de adjuntos recibidos/enviados — clic para previsualizar in-page (PDF, imagen...).
 *  Si los adjuntos están en formato legacy (solo nombres, sin file_id), permite
 *  re-descargarlos desde IMAP llamando a `onRefetch`. */
export function AttachmentChips({ attachments, testIdPrefix = "att", onRefetch }) {
  const [previewIdx, setPreviewIdx] = useState(null);
  const [refetching, setRefetching] = useState(false);
  const items = attachments || [];
  if (!items.length) return null;
  const hasLegacy = items.some((a) => typeof a === "string" || !a.file_id);
  // Sólo los items con file_id se pueden previsualizar — calculamos el índice traducido
  const previewable = items.filter((a) => typeof a !== "string" && a?.file_id);

  const openPreview = (att) => {
    const idx = previewable.findIndex((p) => p.file_id === att.file_id);
    if (idx >= 0) setPreviewIdx(idx);
  };

  const refetch = async () => {
    if (!onRefetch) return;
    setRefetching(true);
    try {
      await onRefetch();
      toast.success("Adjuntos recuperados");
    } catch (err) {
      toast.error(err?.response?.data?.detail || "No se pudieron recuperar los adjuntos");
    } finally {
      setRefetching(false);
    }
  };

  return (
    <>
      <div className="flex flex-wrap items-center gap-1.5">
        {items.map((a, i) => {
          const isLegacy = typeof a === "string" || !a.file_id;
          const name = typeof a === "string" ? a : a.filename;
          return (
            <button
              key={i}
              type="button"
              disabled={isLegacy}
              onClick={() => !isLegacy && openPreview(a)}
              title={isLegacy ? "Adjunto no descargado — pulsa 'Recuperar adjuntos'" : `Previsualizar ${name}`}
              className={`inline-flex items-center gap-1.5 border rounded-md px-2 py-1 text-[11px] transition-colors ${
                isLegacy
                  ? "bg-gray-50 border-gray-200 text-gray-400 cursor-default"
                  : "bg-white border-blue-200 text-blue-700 hover:bg-blue-50 cursor-pointer"
              }`}
              data-testid={`${testIdPrefix}-chip-${i}`}
            >
              <FileText size={11} />
              <span className="max-w-[200px] truncate">{name}</span>
              {!isLegacy && a.size != null && <span className="text-gray-400">{(a.size / 1024).toFixed(0)} KB</span>}
            </button>
          );
        })}
        {hasLegacy && onRefetch && (
          <button
            type="button"
            onClick={refetch}
            disabled={refetching}
            title="Re-descarga los adjuntos desde el buzón IMAP"
            className="inline-flex items-center gap-1.5 border border-dashed border-violet-300 rounded-md px-2 py-1 text-[11px] text-violet-700 hover:bg-violet-50 transition-colors"
            data-testid={`${testIdPrefix}-refetch`}
          >
            {refetching ? <Loader2 size={11} className="animate-spin" /> : <RefreshCw size={11} />}
            {refetching ? "Recuperando…" : "Recuperar adjuntos"}
          </button>
        )}
      </div>
      <AttachmentPreview
        items={previewable}
        index={previewIdx}
        onOpenChange={(v) => !v && setPreviewIdx(null)}
        onIndexChange={setPreviewIdx}
      />
    </>
  );
}

/** Selector de adjuntos para correos salientes: sube a la plataforma y devuelve metadatos. */
export function AttachmentPicker({ attachments, onChange, compact = false, testIdPrefix = "attpick" }) {
  const inputRef = useRef(null);
  const [uploading, setUploading] = useState(false);
  const items = attachments || [];

  const onFiles = async (e) => {
    const files = Array.from(e.target.files || []);
    if (!files.length) return;
    setUploading(true);
    const uploaded = [];
    for (const f of files) {
      if (f.size > 15 * 1024 * 1024) {
        toast.error(`${f.name} supera el máximo de 15 MB`);
        continue;
      }
      try {
        const fd = new FormData();
        fd.append("file", f);
        const { data } = await api.post("/attachments/upload", fd, {
          headers: { "Content-Type": "multipart/form-data" },
        });
        uploaded.push(data);
      } catch (err) {
        toast.error(err?.response?.data?.detail || `No se pudo subir ${f.name}`);
      }
    }
    if (uploaded.length) onChange([...items, ...uploaded]);
    setUploading(false);
    if (inputRef.current) inputRef.current.value = "";
  };

  const remove = (fid) => onChange(items.filter((a) => a.file_id !== fid));

  return (
    <div className={compact ? "space-y-1" : "space-y-1.5"}>
      <input ref={inputRef} type="file" multiple className="hidden" onChange={onFiles}
        data-testid={`${testIdPrefix}-input`} />
      <div className="flex flex-wrap items-center gap-1.5">
        <button
          type="button"
          onClick={() => inputRef.current?.click()}
          disabled={uploading}
          className="inline-flex items-center gap-1.5 border border-dashed border-gray-300 rounded-md px-2 py-1 text-[11px] text-gray-600 hover:border-blue-400 hover:text-blue-700 transition-colors"
          data-testid={`${testIdPrefix}-button`}
        >
          {uploading ? <Loader2 size={11} className="animate-spin" /> : <Paperclip size={11} />}
          {uploading ? "Subiendo…" : "Adjuntar archivo"}
        </button>
        {items.map((a) => (
          <span key={a.file_id}
            className="inline-flex items-center gap-1.5 bg-blue-50 border border-blue-200 text-blue-800 rounded-md px-2 py-1 text-[11px]"
            data-testid={`${testIdPrefix}-item-${a.file_id}`}>
            <FileText size={11} />
            <span className="max-w-[180px] truncate">{a.filename}</span>
            <span className="text-blue-400">{fmtSize(a.size)}</span>
            <button type="button" onClick={() => remove(a.file_id)} className="text-blue-400 hover:text-red-600"
              data-testid={`${testIdPrefix}-remove-${a.file_id}`}>
              <X size={11} />
            </button>
          </span>
        ))}
      </div>
    </div>
  );
}
